<template>
    <transition name="fade">
        <div class="share-modal">
            <div class="modal-backdrop" @click="closeByBackdrop">
                <div class="modal-content relative">
                    <InnerLoader v-if="isLoading" />

                    <div class="modal-header">
                        <div class="text-right">
                            <i
                                v-if="showCloseButton"
                                @click="closeModal"
                                class="fas fa-times text-xl shadow-text-none cursor-pointer"
                            ></i>
                        </div>
                    </div>

                    <div class="modal-body">
                        <div v-if="!isSubmit" class="flex flex-col items-center quiz-item">
                            <h2 class="font-size-22 text-center shadow-text-none mb-2 mt-0">
                                {{ $t('share_popup_header') }}
                            </h2>
                            <p
                                v-html="$t('share_popup_description')"
                                class="font-size-14 shadow-text-none text-center mb-4"
                            ></p>

                            <div
                                :style="{ 'background-image': 'url(' + imagePath + ')' }"
                                class="relative overflow-hidden system-border rounded-lg bg-cover bg-center bg-norepeat w-48 h-48 mb-4"
                            ></div>

                            <div v-if="users.length > 0" class="w-full email-container mb-4 p-4 overflow-hidden">
                                <h4 class="font-size-18 shadow-text-none mb-2 mt-0 text-center">
                                    {{ $t('share_popup_header_user_shared') }}
                                </h4>
                                <div class="flex flex-wrap w-full max-w-md justify-center">
                                    <div
                                        v-for="(item, index) in users"
                                        :key="index"
                                        class="email-item shadow-text-none font-size-16 flex items-center m-1"
                                    >
                                        <span class="inline-block mr-2">{{ item.name }}</span>
                                        <i
                                            @click="removeEmailShared(item._id.$oid, item.email)"
                                            class="fas fa-times-circle font-size-22 cursor-pointer text-red"
                                        ></i>
                                    </div>
                                </div>
                            </div>

                            <div v-if="!isApproved">
                                <p class="font-size-16 mb-4 text-center shadow-text-none">
                                    {{ $t('share_popup_anyone_can') }}:
                                </p>
                                <div class="access-options-box">
                                    <XCheckbox
                                        :checked="true"
                                        :square="true"
                                        :disabled="true"
                                        type="checkbox"
                                        size="lg"
                                        theme="dark"
                                        class="flex-1 font-size-16 shadow-text-none disabled"
                                    >
                                        {{ $t('share_popup_checkbox_can_use') }}
                                    </XCheckbox>
                                    <XCheckbox
                                        @input="updateShareSetting('read')"
                                        :checked="canRead"
                                        :square="true"
                                        :disabled="canEdit"
                                        type="checkbox"
                                        size="lg"
                                        theme="dark"
                                        :class="{ 'flex-1 font-size-16 shadow-text-none': true, disabled: canEdit }"
                                    >
                                        {{ $t('share_popup_checkbox_can_read') }}
                                    </XCheckbox>
                                    <XCheckbox
                                        @input="updateShareSetting('edit')"
                                        :checked="canEdit"
                                        :square="true"
                                        type="checkbox"
                                        size="lg"
                                        theme="dark"
                                        class="flex-1 font-size-16 shadow-text-none"
                                    >
                                        {{ $t('share_popup_checkbox_can_edit') }}
                                    </XCheckbox>
                                    <XCheckbox
                                        @input="updateShareSetting('access_report')"
                                        :checked="canAccessReport"
                                        :square="true"
                                        type="checkbox"
                                        size="lg"
                                        theme="dark"
                                        class="font-size-16"
                                    >
                                        {{ $t('share_popup_checkbox_can_access_report') }}
                                    </XCheckbox>
                                </div>
                            </div>

                            <form
                                @submit.prevent="addEmail()"
                                :class="{
                                    'w-full max-w-xs flex items-start': true,
                                    'mb-4': form.email.length === 0
                                }"
                            >
                                <div class="form-group flex-1">
                                    <input
                                        v-model="email"
                                        v-validate="'required|email'"
                                        :class="{
                                            'auth-input share-email-input': true,
                                            'is-invalid': errors.has('email')
                                        }"
                                        :placeholder="$t('share_popup_placeholder_email')"
                                        type="email"
                                        name="email"
                                    />
                                    <div v-if="errors.has('email')" class="invalid-feedback">
                                        <span class="has-error text-center shadow-text-none">
                                            {{ errors.first('email') }}
                                        </span>
                                    </div>
                                </div>
                                <!-- <button class="add-btn ml-1 shadow-text-none cursor-pointer">
                                    <i class="fas fa-plus"></i>
                                </button> -->
                            </form>

                            <div
                                v-if="form.email.length > 0"
                                class="mb-5 flex flex-wrap w-full max-w-md justify-center"
                            >
                                <div
                                    v-for="(item, index) in form.email"
                                    :key="index"
                                    class="email-item shadow-text-none font-size-16 flex items-center m-1"
                                >
                                    <span class="inline-block mr-2">{{ item }}</span>
                                    <i
                                        @click="removeEmail(index)"
                                        class="fas fa-times-circle font-size-22 cursor-pointer text-red"
                                    ></i>
                                </div>
                            </div>

                            <div class="share-button-box flex flex-wrap justify-center max-w-xs w-full">
                                <div class="tooltip flex-1">
                                    <XButton
                                        @click="copyLink"
                                        @mouseout="copyLinkOut"
                                        class="btn-copy w-full shadow-text-none"
                                    >
                                        <span class="tooltiptext" ref="tooltip">
                                            {{ $t('share_popup_popup_copy') }}
                                        </span>
                                        {{ $t('share_popup_button_copy') }}
                                    </XButton>
                                </div>
                                <XButton
                                    @click="addEmail()"
                                    bg-color="var(--button-background-color)"
                                    color="var(--button-text-color)"
                                    class="send-button shadow-text-none"
                                >
                                    {{ $t('share_popup_button_send') }}
                                </XButton>
                            </div>
                        </div>
                        <div
                            v-if="isSubmit"
                            class="flex flex-col justify-center items-center shadow-text-none"
                            style="min-height: 250px"
                        >
                            <i
                                v-if="success && alreadyShowResult"
                                class="fas fa-paper-plane font-size-60 text-green"
                            ></i>
                            <i
                                v-if="!success && alreadyShowResult"
                                class="fas fa-times-circle font-size-60 text-red"
                            ></i>
                            <h4 class="font-size-22 mt-3 mb-0" v-if="success && alreadyShowResult">
                                {{ $t('share_popup_share_sent') }}
                            </h4>
                            <h4 class="font-size-22 mt-3 mb-0" v-if="!success && alreadyShowResult">
                                {{ errorMessage }}
                            </h4>
                        </div>
                    </div>
                </div>
            </div>

            <input
                :value="shareUrl"
                style="position: absolute; top: -999px; left: -999px; opacity: 0"
                type="text"
                ref="link-to-share"
            />
        </div>
    </transition>
</template>

<script>
import InnerLoader from '@/components/InnerLoader'
import Request from '@/lib/request.js'
import appConfig from '@/app.config.js'
import popup from '@/lib/popup.js'
import { currentUser } from '@/lib/user'
import XCheckbox from '@/components/v2/XCheckbox'
import XButton from '@/components/v2/XButton'

export default {
    name: 'SharePopup',

    components: {
        InnerLoader,
        XCheckbox,
        XButton
    },

    props: {
        showCloseButton: {
            type: Boolean,
            default: true
        },
        id: {
            type: String,
            default: ''
        },
        title: {
            type: String,
            default: ''
        },
        imagePath: {
            type: String,
            default: ''
        },
        currentUser: {
            default: null
        },
        sharedUser: {
            type: Array
        }
    },

    data() {
        return {
            isLoading: false,
            email: '',
            form: {
                id: null,
                email: []
            },
            isSubmit: false,
            success: false,
            alreadyShowResult: false,
            users: [],
            errorMessage: 'Something wrong!',
            shareUrl: '',
            shareToken: '',
            canRead: false,
            canEdit: false,
            canAccessReport: false,
            isApproved: false
        }
    },

    mounted() {
        this.fetchShareUser()
        this.fetchCopyLink()
    },

    watch: {
        canRead(value) {
            this.$store.commit('setCanRead', value)
        },

        canEdit(value) {
            this.$store.commit('setCanEdit', value)
        },

        canAccessReport(value) {
            this.$store.commit('setCanAccessReport', value)
        }
    },

    methods: {
        closeByBackdrop(e) {
            if (!e.target.classList.contains('modal-backdrop')) {
                return
            }

            this.$emit('close')
        },

        closeModal() {
            this.$emit('close')
        },

        addEmail() {
            let validation = 0

            this.$validator.validate().then(result => {
                if (!result) {
                    return false
                }

                this.form.email.forEach(email => {
                    if (email === this.email) {
                        validation++
                    }
                })

                this.users.forEach(user => {
                    if (user.email === this.email) {
                        validation++
                    }
                })

                if (validation > 0) {
                    popup.alert(this.$t('share_popup_alert_already_email'))
                    return false
                }

                this.form.email = [...this.form.email, this.email]
                this.email = ''
                this.share()
            })
        },

        removeEmail(index) {
            this.form.email.splice(index, 1)
        },

        share() {
            let request = new Request()
            let formData = {
                ...this.form,
                id: this.id,
                title: this.title,
                imagePath: this.imagePath,
                nameUser: this.currentUser,
                is_school_role: this.isSchoolRoles,
                token: this.shareToken
            }

            this.isLoading = true
            this.isSubmit = true

            request
                .post(appConfig.api.shareQuiz, formData)
                .then(() => {
                    this.isLoading = false
                    this.success = true
                    this.alreadyShowResult = true
                })
                .catch(err => {
                    this.alreadyShowResult = true
                    this.isLoading = false
                    this.errorMessage = err.response.data.message
                })
        },

        fetchShareUser() {
            let request = new Request()
            let url = appConfig.api.fetchSharedUser.replace(/__id__/, this.id)

            this.isLoading = true

            request
                .get(url)
                .then(res => {
                    this.users = res.data.data
                    this.isLoading = false
                })
                .catch(() => {
                    this.isLoading = false
                })
        },

        removeEmailShared(userId, email) {
            let request = new Request()
            let url = appConfig.api.deleteSharedUser.replace('__id__', this.id)
            let formData = {
                shared_user: []
            }

            formData.shared_user = this.users.map(user => {
                return user._id.$oid
            })

            formData.shared_user = formData.shared_user.filter(item => {
                return item !== userId
            })

            popup.confirm(this.$t('share_popup_alert_delete_email', { email: email }), () => {
                this.isLoading = true
                request
                    .put(url, formData)
                    .then(() => {
                        this.users = this.users.filter(item => {
                            return item._id.$oid !== userId
                        })

                        this.isLoading = false
                    })
                    .catch(() => {
                        this.isLoading = false
                    })
            })
        },

        copyLink() {
            let url = this.$refs['link-to-share']
            let tooltip = this.$refs['tooltip']

            navigator.clipboard.writeText(url.value).then(() => {
                if (tooltip) {
                    tooltip.innerHTML = this.$t('share_popup_popup_copied')
                }

                this.isLoading = false
            })
        },

        fetchCopyLink() {
            let request = new Request()
            // let url = this.$refs['link-to-share']
            let tooltip = this.$refs['tooltip']

            this.isLoading = true
            request
                .post(appConfig.api.shareLink, {
                    quiz_id: this.id
                })
                .then(res => {
                    let token = res.data.data.token

                    this.setShareUrl(token)
                    this.shareToken = token

                    this.canRead = res.data.data.share_setting.can_read || false
                    this.canEdit = res.data.data.share_setting.can_edit || false
                    this.canAccessReport = res.data.data.share_setting.can_access_report || false
                    this.isApproved = res.data.data.approval_status === 'approved' ? true : false
                })
                .catch(() => {
                    if (tooltip) {
                        tooltip.innerHTML = this.$t('share_popup_popup_not_copy')
                    }

                    this.isLoading = false
                })
        },

        copyLinkOut() {
            let tooltip = this.$refs['tooltip']

            if (tooltip) {
                tooltip.innerHTML = this.$t('share_popup_popup_copy')
            }
        },

        setShareUrl(token) {
            if (this.getIsSchoolRoles()) {
                if (currentUser().provider === 'feide') {
                    this.shareUrl = `${appConfig.baseUrl}skoler/set/${this.id}/share/${token}?school=true`
                    return
                }

                this.shareUrl = `${appConfig.baseUrl}school/set/${this.id}/share/${token}?school=true`
                return
            }

            this.shareUrl = `${appConfig.baseUrl}set/${this.id}/share/${token}`
        },

        async updateShareSetting(type) {
            if (this.isApproved) return

            if (type === 'read') {
                if (this.canEdit) return

                this.canRead = !this.canRead

                if (this.canRead) {
                    this.canEdit = false
                }
            }

            if (type === 'edit') {
                this.canEdit = !this.canEdit

                if (this.canEdit) {
                    this.canRead = true
                }
            }

            if (type === 'access_report') {
                this.canAccessReport = !this.canAccessReport
            }

            this.isLoading = true

            try {
                await this.$store.dispatch('updateShareSetting', {
                    id: this.id,
                    can_read: this.canRead,
                    can_edit: this.canEdit,
                    can_access_report: this.canAccessReport,
                    type
                })
            } catch {
                return false
            } finally {
                this.isLoading = false
            }
        }
    }
}
</script>

<style lang="scss">
$input-border-color: #cbcbcb;
$input-border-darker-color: #b6b6b6;

.share-modal {
    font-family: 'Open Sans', sans-serif !important;
    background-color: rgba(0, 0, 0, 0.2);
    width: 100%;
    height: 100%;
    position: fixed;
    top: 0;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: start;
    font-size: 1.2em;
    z-index: 9999;

    .share-popup-header {
        margin-bottom: 3rem;

        @media screen and (max-width: 719px) {
            margin-bottom: 1rem;
        }
    }

    .modal-backdrop {
        width: 100%;
        height: 100%;
        position: fixed;
        top: 0;
        left: 0;
        display: flex;
        justify-content: center;
        align-items: start;
        font-size: 0.5rem;
        z-index: 9999;
        overflow: auto;

        .modal-content {
            background-color: #fff;
            margin-top: 4rem;
            padding: 1rem;
            min-width: 720px;
            overflow: hidden;
            border-radius: 3px;
            box-shadow: 2px 2px 20px rgba(0, 0, 0, 0.6);

            @media screen and (max-width: 719px) {
                min-width: 90%;
                margin: 1rem 0.5rem;
            }

            .modal-body {
                margin: 0 0 2rem 0;

                @media screen and (max-width: 719px) {
                    margin: 0 0 1rem 0;
                }

                .access-options-box {
                    display: flex;
                    gap: 1rem;
                    margin-block: 1rem;

                    @media screen and (max-width: 719px) {
                        display: grid;
                        grid-template-columns: repeat(2, 1fr);
                    }
                }

                .share-button-box {
                    column-gap: 1rem;

                    .send-button {
                        min-width: 120px;
                    }
                }
            }
        }
    }

    .auth-input.share-email-input {
        display: block;
        width: 100%;
        border: 1px solid $input-border-color;
        font-size: 1rem;
        padding: 0.8rem;
        border-radius: 3px;
        text-align: center;

        &::placeholder {
            font-weight: bold;
            text-align: center;
            color: $input-border-color;
        }

        &.button {
            cursor: pointer;
            color: $input-border-darker-color;
            font-weight: bold;
            background-color: #fff;

            &:hover {
                background-color: #f0f0f0;
            }

            &.button-success {
                background-color: #009d1a;
                color: #fff;

                &:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                &:hover {
                    background-color: #018f19;
                    color: #fff;
                }
            }
        }

        &.is-invalid {
            border-color: #ef0000 !important;
        }
    }

    .invalid-feedback {
        color: #ef0000;
        font-size: 0.7rem;
        text-align: center;
        margin-top: 0.4rem;
    }

    .add-btn {
        font-size: 1rem;
        padding: 0.8rem;
        border-radius: 3px;
        border: 1px solid $input-border-color;
    }

    .email-item {
        padding: 0.5rem 1rem;
        padding-right: 0.5rem;
        border: 1px solid $input-border-color;
        border-radius: 30px;
        background-color: white;
    }

    .email-container {
        border: 1px solid #ccc;
        border-radius: 6px;
        background-color: #f5f5f5;
    }
}

.btn-copy {
    background-color: transparent !important;
    color: var(--button-background-color) !important;
    border: 2px solid var(--button-background-color) !important;
    padding: calc(0.5rem - 2px) calc(1.5rem - 2px);
    transition: 0.2s;

    &:hover {
        background-color: var(--button-background-color-hover) !important;
        color: var(--button-text-color) !important;
    }
}

.tooltip {
    position: relative;
    display: inline-block;

    &:hover .tooltiptext {
        visibility: visible;
        opacity: 1;
    }

    .tooltiptext {
        visibility: hidden;
        min-width: 180px;
        background-color: rgba(0, 0, 0, 0.7);
        color: #fff;
        text-align: center;
        border-radius: 6px;
        padding: 7px 5px;
        position: absolute;
        z-index: 1;
        bottom: 120%;
        left: 50%;
        transform: translateX(-50%);
        opacity: 0;
        -webkit-transition: opacity 0.3s;
        font-size: 0.9rem;
        transition: opacity 0.3s;

        &::after {
            content: '';
            position: absolute;
            top: 100%;
            left: 50%;
            margin-left: -5px;
            border-width: 5px;
            border-style: solid;
            border-color: rgba(0, 0, 0, 0.7) transparent transparent transparent;
        }
    }
}
</style>
